import { ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { AppBar, Box, Toolbar, useMediaQuery, AppBarProps } from '@mui/material';

// material-ui icons
import MenuOpenIcon from '@mui/icons-material/MenuOpenRounded';

// project import
import AppBarStyled from './AppBarStyled';
import HeaderContent from './HeaderContent';
import IconButton from 'components/@extended/IconButton';
import useNativeAppStyling from 'hooks/useNativeAppStyling';
import useAppFunctionalityConfig from 'hooks/useAppFunctionalityConfig';
import Logo from 'components/logo';
import ToolbarDivider from 'components/display/toolbarDivider';

// ==============================|| MAIN LAYOUT - HEADER ||============================== //

interface Props {
  open: boolean;
  drawerDisabled?: boolean;
  handleDrawerToggle?: () => void;
}

const Header = ({ open, drawerDisabled, handleDrawerToggle }: Props) => {
  const theme = useTheme();
  const matchDownMD = useMediaQuery(theme.breakpoints.down('lg'));
  const { statusBarOffset } = useNativeAppStyling();
  const { isAudienceMode } = useAppFunctionalityConfig();
  const location = useLocation();

  // header content
  const headerContent = useMemo(() => <HeaderContent />, []);

  const iconBackColorOpen = theme.palette.mode === 'dark' ? 'grey.200' : 'primary.lighter';// 'grey.300';
  const iconBackColor = theme.palette.mode === 'dark' ? 'background.default' : 'grey.100';

  // common header
  const mainHeader: ReactNode = (
    <Toolbar disableGutters={isAudienceMode} sx={{ pl: isAudienceMode ? 3 : 1, pr: 1 }}>
      {isAudienceMode && (
        <>
          <Logo
            to={location.pathname + location.search}
            sx={{ width: 'auto', height: 35, flexShrink: 0 }}
          />
          <Box sx={{ height: 36, flexShrink: 0 }}>
            <ToolbarDivider orientation="horizontal" />
          </Box>
        </>
      )}
      {!isAudienceMode && <IconButton
        aria-label="open drawer"
        disabled={drawerDisabled}
        onClick={handleDrawerToggle}
        edge="start"
        color="secondary"
        variant="light"
        sx={{ color: 'text.primary', bgcolor: open ? iconBackColorOpen : iconBackColor, ml: { xs: 0, lg: -2 } }}
      >
        <MenuOpenIcon fontSize='small' style={{ transform: open ? 'rotate(0deg)' : 'rotate(-180deg)', transition: 'transform 0.2s linear' }} />
      </IconButton>}
      {headerContent}
    </Toolbar>
  );

  // app-bar params
  const appBar: AppBarProps = {
    position: 'fixed',
    color: 'inherit',
    elevation: 0,
    sx: {
      borderBottom: `1px solid ${theme.palette.divider}`,
      zIndex: 1200,
      pt: statusBarOffset,
      // boxShadow: theme.customShadows.z1
    }
  };

  const showAppBarStyled = !matchDownMD && !isAudienceMode
  return (
    <>
      {showAppBarStyled ? (
        <AppBarStyled open={open} {...appBar}>
          {mainHeader}
        </AppBarStyled>
      ) : (
        <AppBar {...appBar}>{mainHeader}</AppBar>
      )}
    </>
  );
};

export default Header;
